import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { FLAGS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo, CircleNotch } from "@phosphor-icons/react";

export default function Register() {
  const [form, setForm] = useState({ name: "", username: "", email: "", phone: "", password: "" });
  const [country, setCountry] = useState("US");
  const [countries, setCountries] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  // Preserve any plan the visitor picked before signing up.
  const params = new URLSearchParams(window.location.search);
  const planParam = params.get("plan");
  const billingParam = params.get("billing");

  useEffect(() => {
    api.get("/countries").then(({ data }) => {
      setCountries(data);
      const codes = data.map((c) => c.code);
      const locale = (navigator.language || "").split("-")[1]?.toUpperCase();
      if (locale && codes.includes(locale)) setCountry(locale);
      api.get("/auth/geo").then(({ data: g }) => {
        if (g.supported && codes.includes(g.ip_country)) setCountry(g.ip_country);
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) { toast.error("Please accept the Terms of Service and Privacy Policy."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, country, accepted_terms: true });
      setUser(data);
      toast.success("Vault created! Check your email to verify.");
      const next = new URLSearchParams(window.location.search).get("next");
      if (next && next.startsWith("/")) {
        navigate(next);
      } else if (planParam && planParam !== "free") {
        const q = new URLSearchParams({ plan: planParam, ...(billingParam ? { billing: billingParam } : {}) });
        navigate(`/dashboard/plans?${q.toString()}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Create your vault" subtitle="Start protecting your important documents.">
      <Seo title="Create your vault" description="Create your secure DuruVault to store, protect, sign, verify and share your important documents." path="/register" />
      <form onSubmit={submit} className="space-y-4" aria-label="Create account">
        <div>
          <Label htmlFor="reg-country" className="text-white/70">Country</Label>
          <select id="reg-country" name="country" autoComplete="country" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="register-country-select"
            className="mt-1.5 w-full rounded-md bg-white/5 border border-white/15 text-white p-2.5 text-sm focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/40 outline-none">
            {countries.map((c) => <option key={c.code} value={c.code} className="bg-[#20242a]">{FLAGS[c.code] || "🏳️"} {c.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="reg-name" className="text-white/70">Full name</Label>
            <Input id="reg-name" name="name" autoComplete="name" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="mt-2 bg-white/5 border-white/15 text-white" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="reg-username" className="text-white/70">Username</Label>
            <Input id="reg-username" name="username" autoComplete="username" value={form.username} onChange={set("username")} required placeholder="janedoe" className="mt-2 bg-white/5 border-white/15 text-white" data-testid="register-username-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="reg-email" className="text-white/70">Email</Label>
          <Input id="reg-email" name="email" type="email" autoComplete="email" inputMode="email" value={form.email} onChange={set("email")} required placeholder="you@example.com" className="mt-2 bg-white/5 border-white/15 text-white" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="reg-phone" className="text-white/70">Phone <span className="text-white/30">(optional)</span></Label>
          <Input id="reg-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" className="mt-2 bg-white/5 border-white/15 text-white" data-testid="register-phone-input" />
        </div>
        <div>
          <Label htmlFor="reg-password" className="text-white/70">Password</Label>
          <Input id="reg-password" name="password" type="password" autoComplete="new-password" value={form.password} onChange={set("password")} required placeholder="At least 8 characters" className="mt-2 bg-white/5 border-white/15 text-white" data-testid="register-password-input" />
        </div>
        <label className="flex items-start gap-2.5 text-sm text-white/60 cursor-pointer" htmlFor="reg-terms">
          <input id="reg-terms" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 accent-primary w-4 h-4" data-testid="register-terms-checkbox" />
          <span>I have read and agree to the{" "}
            <Link to="/legal/terms" target="_blank" className="text-primary hover:underline" data-testid="register-terms-link">Terms of Service</Link>{" "}and{" "}
            <Link to="/legal/privacy" target="_blank" className="text-primary hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
          </span>
        </label>
        <Button type="submit" disabled={loading || !accepted} className="w-full rounded-full h-11" data-testid="register-submit-btn">
          {loading ? <CircleNotch className="animate-spin" /> : "Create vault"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-6"><div className="h-px bg-white/10 flex-1" /><span className="text-white/40 text-xs">or</span><div className="h-px bg-white/10 flex-1" /></div>
      <Button onClick={google} variant="outline" className="w-full rounded-full h-11 bg-transparent border-white/15 text-white hover:bg-white/10" data-testid="register-google-btn">
        <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
      </Button>
      <p className="mt-8 text-center text-sm text-white/50">
        Already have a vault? <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
